import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import _ from "lodash";
import CSSModules from "lib/utils/css-modules";

import st from "lib/utils/index";
import { ProductScheduleDecorator, ProductScheduleSlotDecorator } from "lib/decorators/index";
import { saleWindowFromVariantDefaults } from "lib/utils/saleWindow";
import { SlotsCalendar, FormControl, Button, MainView, HintView } from "lib/components/index";
import { open as openShowSlotModal } from "modules/calendar/ShowSlotModal/index";
import {
  stateSelector,
  initializeView,
  updateSlot,
  removeSlot,
  openSlotForm,
  closeForm,
  validate,
  save,
  setDateRange,
  setIsScheduleBeingEdited,
} from "./index";
import styles from "./StepAvailability.less";
import Hint from "./StepAvailabilityHint";
import ProductScheduleSlotForm from "./ProductScheduleSlotForm";
import createConfirmSlotWithOrWithoutFutureModal from "./ConfirmSlotWithOrWithoutFutureModal";

function slotSortByFn(slot) {
  return [new Date(slot.starts_at).getTime(), slot.id, slot.uuid];
}

function getMinSlotStartsAt({ editedSlot, initialEditedSlot }) {
  let m;
  if (initialEditedSlot && editedSlot) {
    m = initialEditedSlot.starts_at_moment.isBefore(editedSlot.starts_at_moment)
      ? initialEditedSlot.starts_at_moment
      : editedSlot.starts_at_moment;
  } else {
    m =
      (initialEditedSlot && initialEditedSlot.starts_at_moment) ||
      (editedSlot.starts_at && editedSlot.starts_at_moment);
  }
  return m && m.format(moment.RUBY_DATETIME_FORMAT);
}

function regenerateScheduleSlots({ editedSlot, initialEditedSlot, schedule, existingScheduleSlots }) {
  schedule = new ProductScheduleDecorator({
    ...schedule,
    _start_dates_assignation_type: undefined,
    _start_times_assignation_type: undefined,
  });

  const minSlotStartsAtTimestamp = new Date(getMinSlotStartsAt({ editedSlot, initialEditedSlot })).getTime();
  const newStartsAts = _(schedule.start_dates)
    .map((start_date) =>
      (schedule.includes_time ? schedule.start_times : ["00:00"]).map((start_time) =>
        moment.tz(
          `${start_date}T${start_time}:00.000`,
          moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT,
          schedule.time_zone_name
        )
      )
    )
    .flatten()
    // .filter(date => !minSlotStartsAt || date.valueOf() >= minSlotStartsAt.getTime())
    .map((date) => date.format(moment.RUBY_DATETIME_FORMAT))
    .value();

  const newSlots = newStartsAts.map((starts_at) => {
    const slot = existingScheduleSlots.find(
      (sl) =>
        sl.starts_at === starts_at &&
        sl.time_duration === schedule.time_duration &&
        sl.starts_at_moment.valueOf() >= minSlotStartsAtTimestamp
    ) || {
      uuid: st.generateUuid(),
      company_id: editedSlot.company_id,
      product_id: editedSlot.product_id,
      product_variant_id: schedule.product_variant_id,
      time_zone_name: schedule.time_zone_name,
      total_stock_used: 0,
    };

    let ends_at = null;
    if (schedule.time_duration && schedule.includes_time) {
      ends_at = moment
        .tz(starts_at, schedule.time_zone_name)
        .add(schedule.time_duration, "seconds")
        .format(moment.RUBY_DATETIME_FORMAT);
    } else if (schedule.time_duration && !schedule.includes_time) {
      ends_at = moment
        .tz(starts_at, schedule.time_zone_name)
        .add(schedule.time_duration / 86400 - 1, "days")
        .endOf("day")
        .format(moment.RUBY_DATETIME_FORMAT);
    }

    return ProductScheduleSlotDecorator.build({
      ...slot,
      product_schedule: schedule,
      active: schedule.active,
      confirmed: schedule.confirmed,
      availability_type: schedule.availability_type,
      starts_at,
      ends_at,
      includes_time: schedule.includes_time,
      total_stock_limit: schedule.total_stock_limit,
      note: schedule.note,
      default_product_pricelist_item_price: editedSlot.default_product_pricelist_item_price,
      stock_type: editedSlot.stock_type,
      price_pricings: schedule.price_pricings,
    });
  });
  const newSlotsIds = newSlots.map((sl) => sl.id);

  const staleSlots = existingScheduleSlots
    .filter((sl) => !newSlotsIds.includes(sl.id))
    .map(
      (slot) =>
        new ProductScheduleSlotDecorator({
          ...slot,
          product_schedule: null,
          active: new Date(slot.starts_at).getTime() < minSlotStartsAtTimestamp ? slot.active : false,
        })
    );

  return _.sortBy(newSlots.concat(staleSlots), slotSortByFn);
}

function generateSlots({ slots, editedSlot, initialEditedSlot, forWholeSchedule }) {
  slots = _.sortBy(slots, slotSortByFn);
  let editedSlots = [];
  let slotsToRemove = [];
  let allSlotsWithEditedOnes = [];
  let editedScheduleSlots = [];
  const existingScheduleSlots = slots.filter((sl) => sl.product_schedule_id === editedSlot.product_schedule_id);
  if (forWholeSchedule && editedSlot.product_schedule) {
    editedSlots = regenerateScheduleSlots({
      editedSlot: editedSlot,
      initialEditedSlot: initialEditedSlot,
      schedule: editedSlot.product_schedule,
      existingScheduleSlots,
    });
    editedScheduleSlots = editedSlots.filter((sl) => sl.product_schedule_id === editedSlot.product_schedule_id);
    const editedScheduleSlotsIds = editedScheduleSlots.map((sl) => sl.uuid);
    slotsToRemove = existingScheduleSlots.filter((sl) => !editedScheduleSlotsIds.includes(sl.uuid));
  } else {
    editedScheduleSlots = editedSlots = [editedSlot];
  }
  allSlotsWithEditedOnes = _(editedSlots)
    .concat(slots)
    .uniqBy((sl) => sl.uuid)
    .sortBy(slotSortByFn)
    .value();
  return {
    // existing slots in this schedule
    existingScheduleSlots,
    // slots we are currently working on (including the existing deactivated/nondeactivated and new ones)
    editedSlots,
    // existing slots in this schedule
    editedScheduleSlots,
    // all the slots merged with the new edited ones (without duplicates)
    allSlotsWithEditedOnes,
    // existing slots in this schedule, that will be removed/deactivated because of this edit
    slotsToRemove,
  };
}

const CalendarFilters = ({ product, filters: { product_variant_id }, onChange }) => (
  <FormControl.Select
    hidden={product.product_variants.length <= 1}
    value={product_variant_id}
    onChange={(product_variant_id) => onChange({ product_variant_id })}
    options={product.product_variants.map((pv) => [pv.id, pv.name])}
    placeholder={I18n.t("papp.products.EditProductView.StepAvailability.all_product_variants")}
    includeBlank
    className="u-largeWidth"
  />
);

@connect(
  (state) => ({
    company: state.auth.company.model,
    slots: stateSelector(state).slots,
    editedSlot: stateSelector(state).editedSlot,
    initialEditedSlot: stateSelector(state).initialEditedSlot,
    isScheduleBeingEdited: stateSelector(state).isScheduleBeingEdited,
    errors: stateSelector(state).errors,
    valid: stateSelector(state).valid,
    fetchDone: stateSelector(state).fetchDone,
    fetchLoading: stateSelector(state).fetchLoading,
    fetchFailed: stateSelector(state).fetchFailed,
    submitLoading: stateSelector(state).submitLoading,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        initializeView,
        updateSlot,
        removeSlot,
        openSlotForm,
        closeForm,
        validate,
        save,
        setDateRange,
        setIsScheduleBeingEdited,
        openShowSlotModal,
      },
      dispatch
    )
)
@CSSModules(styles)
export default class StepAvailabilityForm extends React.PureComponent {
  state = {
    filters: {
      product_variant_id: null,
    },
  };

  componentDidMount() {
    this._respondToProps();
  }

  handleCalendarNavigate = ({ minVisibleDate, maxVisibleDate }) => {
    this.props.setDateRange({
      starts_at_gte: minVisibleDate,
      starts_at_lte: maxVisibleDate,
    });
  };

  componentDidUpdate(prevProps) {
    this._respondToProps(prevProps);
  }

  _respondToProps(prevProps) {
    const { product, fetchDone, initializeView } = this.props;
    if (!fetchDone && (!prevProps || prevProps.product.id !== product.id)) {
      initializeView(product);
    }
  }

  handleAddNewScheduleClick(type = "single") {
    const { product, openSlotForm } = this.props;
    const product_variant =
      product.product_variants.find((pv) => pv.id === this.state.filters.product_variant_id) ||
      product.product_variants[0];
    let newSlot = ProductScheduleSlotDecorator.build({
      company_id: product.company.id,
      product_id: product.id,
      product_variant_id: product_variant.id,
      time_zone_name: product.company.time_zone_name,
      total_stock_limit: product_variant.default_total_stock_limit,
      availability_type: product_variant.default_requires_company_approval
        ? "available_with_company_approval"
        : "available_with_instant_booking",
      stock_type: product_variant.stock_type,
      ...saleWindowFromVariantDefaults(product_variant),
    });
    if (type === "single") {
      newSlot = ProductScheduleSlotDecorator.build({
        ...newSlot,
        _hideRecurrenceAccordion: true,
        _showAllAccordions: true,
      });
    } else if (type === "multiple") {
      newSlot = ProductScheduleSlotDecorator.build({
        ...newSlot,
        _hideRecurrenceAccordion: true,
        _showRecurrenceOutside: true,
        _forceStartDateRecurrence: true,
        _forceScheduleEdit: true,
        product_schedule: {
          uuid: st.generateUuid(),
          start_dates_assignation_type: "normal",
          start_dates: [],
          start_date_recursion_from: null,
          start_date_recursion_to: null,
          start_date_recursion_wdays: [],
          start_times_assignation_type: null,
          start_times: [],
          start_time_recursion_from: null,
          start_time_recursion_to: null,
          time_duration: null,
          time_zone_name: newSlot.time_zone_name,
          product_variant_id: newSlot.product_variant_id,
          active: newSlot.active,
          availability_type: newSlot.availability_type,
          total_stock_limit: newSlot.total_stock_limit,
          note: newSlot.note,
          price_pricings: newSlot.price_pricings,
        },
      });
    }
    openSlotForm(newSlot);
  }

  renderCalendarContents() {
    const {
      readOnlyAccess,
      company,
      product,
      editedSlot,
      initialEditedSlot,
      isScheduleBeingEdited,
      updateSlot,
      openSlotForm,
      closeForm,
      fetchLoading,
    } = this.props;
    const filteredSlots = this.props.slots.filter(
      (sl) => !this.state.filters.product_variant_id || sl.product_variant_id === this.state.filters.product_variant_id
    );
    const { editedSlots, allSlotsWithEditedOnes, editedScheduleSlots } = editedSlot
      ? generateSlots({
          slots: filteredSlots,
          editedSlot,
          initialEditedSlot,
          forWholeSchedule: isScheduleBeingEdited,
        })
      : {
          editedSlots: undefined,
          allSlotsWithEditedOnes: filteredSlots,
          editedScheduleSlots: undefined,
        };

    const handleCalendarSlotSelect = (slot) => {
      if (!slot || (editedSlot && slot.uuid === editedSlot.uuid)) {
        return closeForm();
      }
      if (
        editedSlot &&
        editedSlots.find((sl) => sl.uuid === slot.uuid) &&
        !editedScheduleSlots.find((sl) => sl.uuid === slot.uuid)
      ) {
        slot = filteredSlots.find((sl) => sl.uuid === slot.uuid);
      }
      openSlotForm(slot);
    };

    const handleCalendarDateRangeSelect = ({ starts_at, ends_at, dates }) => {
      const start = moment.tz(
        moment.parseZone(starts_at).format(moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT),
        moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT,
        company.time_zone_name
      );
      let end = moment.tz(
        moment.parseZone(ends_at).format(moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT),
        moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT,
        company.time_zone_name
      );
      const includes_time = end.format(moment.TIME_FORMAT) !== "00:00";
      if (!includes_time) {
        end = end.endOf("day").seconds(59).milliseconds(0);
      }
      dates = dates.map((d) =>
        moment.tz(d, moment.RUBY_DATE_FORMAT, company.time_zone_name).format(moment.RUBY_DATE_FORMAT)
      );
      // if (editedSlot && editedSlot.product_schedule) {
      //   // TODO migrate to updateSlot
      //   // // Add selected dates from the schedule (or remove, if all of them are already included)
      //   // if (editedSchedule.start_dates_assignation_type === "recursion") {
      //   //   updateSchedule(new ProductScheduleDecorator({
      //   //     ...editedSchedule,
      //   //     start_date_recursion_range: [
      //   //       starts_at.format(moment.RUBY_DATE_FORMAT),
      //   //       ends_at.format(moment.RUBY_DATE_FORMAT)
      //   //     ]
      //   //   }))
      //   // } else {
      //   //   updateSchedule(new ProductScheduleDecorator({
      //   //     ...editedSchedule,
      //   //     start_dates: dates.find(d => !editedSchedule.start_dates.includes(d))
      //   //       ? editedSchedule.start_dates.concat(dates)
      //   //       : editedSchedule.start_dates.filter(d => !dates.includes(d))
      //   //   }))
      //   // }
      // } else if (editedSlot) {
      //   // Set the slot's datetime to the selected date range
      //   if (editedSlot.includes_time) {
      //     updateSlot(new ProductScheduleSlotDecorator({
      //       ...editedSlot,
      //       start_date: starts_at.format(moment.RUBY_DATE_FORMAT),
      //       end_date: ends_at.format(moment.RUBY_DATE_FORMAT),
      //     }))
      //   } else {
      //     updateSlot(new ProductScheduleSlotDecorator({
      //       ...editedSlot,
      //       starts_at: starts_at.format(moment.RUBY_DATETIME_FORMAT),
      //       ends_at: ends_at.format(moment.RUBY_DATETIME_FORMAT),
      //     }))
      //   }
      // } else {
      // Create a schedule with given daterange
      const product_variant =
        product.product_variants.find((pv) => pv.id === this.state.filters.product_variant_id) ||
        product.product_variants[0];
      openSlotForm(
        ProductScheduleSlotDecorator.build({
          company_id: product.company.id,
          product_id: product.id,
          product_variant_id: product_variant.id,
          time_zone_name: product.company.time_zone_name,
          starts_at: start.format(moment.RUBY_DATETIME_FORMAT),
          ends_at: end.format(moment.RUBY_DATETIME_FORMAT),
          includes_time,
          total_stock_limit: product_variant.default_total_stock_limit,
          availability_type: product_variant.default_requires_company_approval
            ? "available_with_company_approval"
            : "available_with_instant_booking",
          stock_type: product_variant.stock_type,
          ...saleWindowFromVariantDefaults(product_variant),
        })
      );
      // }
    };

    const handleCalendarSlotMove = ({ slot, starts_at, ends_at }) => {
      if (
        editedSlot &&
        editedSlots.find((sl) => sl.uuid === slot.uuid) &&
        !(editedScheduleSlots || []).find((sl) => sl.uuid === slot.uuid)
      ) {
        slot = filteredSlots.find((sl) => sl.uuid === slot.uuid);
      }
      const start = moment.tz(
        moment(starts_at).format(moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT),
        moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT,
        company.time_zone_name
      );
      const end = moment.tz(
        moment(ends_at).format(moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT),
        moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT,
        company.time_zone_name
      );
      openSlotForm(
        new ProductScheduleSlotDecorator({
          ...slot,
          starts_at: start.format(moment.RUBY_DATETIME_FORMAT),
          ends_at: end.format(moment.RUBY_DATETIME_FORMAT),
        })
      );
    };

    const filters = (
      <CalendarFilters
        product={product}
        filters={this.state.filters}
        onChange={(filters) => this.setState({ filters })}
      />
    );

    const modifiers = {
      past: (event) => !isScheduleBeingEdited && moment(event.starts_at).isBefore(),
      active: (event) => event.active,
      inactive: (event) => !event.active,
      selected: (event) => selectedSlotsIds && selectedSlotsIds.includes(event.id),
      unselected: (event) => selectedSlotsIds && !selectedSlotsIds.includes(event.id),
      opaque: (event) => editedSlotsIds && !editedSlotsIds.includes(event.id),
    };

    const selectedSlotsIds = editedScheduleSlots && editedScheduleSlots.map((sl) => sl.id);
    const editedSlotsIds = editedSlots && editedSlots.map((sl) => sl.id);
    return (
      <SlotsCalendar
        showLoadingDimmer={fetchLoading}
        slots={allSlotsWithEditedOnes}
        modifiers={modifiers}
        onSlotSelect={handleCalendarSlotSelect}
        onDateRangeSelect={!readOnlyAccess ? handleCalendarDateRangeSelect : undefined}
        onSlotMove={!readOnlyAccess ? handleCalendarSlotMove : undefined}
        onNavigate={this.handleCalendarNavigate}
        filters={filters}
        showProductName={false}
      />
    );
  }

  handleSlotDelete() {
    const { slots, initialEditedSlot, removeSlot, isScheduleBeingEdited, setIsScheduleBeingEdited, openShowSlotModal } =
      this.props;
    if (!initialEditedSlot) {
      throw "handleSlotDelete called when initialEditedSlot is empty!";
    }
    const editedSlot = new ProductScheduleSlotDecorator(initialEditedSlot);
    editedSlot.normalizeSchedule();
    const { editedScheduleSlots } = generateSlots({
      slots,
      editedSlot,
      initialEditedSlot,
      forWholeSchedule: true,
    });
    const shownInitialFutureDate =
      initialEditedSlot && st.formatDate(getMinSlotStartsAt({ editedSlot, initialEditedSlot }));

    if (isScheduleBeingEdited && editedScheduleSlots.length > 1) {
      st.confirm({
        header: I18n.t("papp.products.EditProductView.StepAvailability.remove_all_slots_confirm.title"),
        content: I18n.t("papp.products.EditProductView.StepAvailability.remove_all_slots_confirm.message"),
        confirmButton: I18n.t("papp.products.EditProductView.StepAvailability.remove_all_slots_confirm.confirm", {
          all_slots_initial_date: shownInitialFutureDate,
        }),
      }).then((data) => {
        if (data) {
          const { mainConfirm } = data;
          removeSlot(initialEditedSlot, { withAllFutureSlots: mainConfirm });
        }
      });
    } else if (editedScheduleSlots.length > 1) {
      createConfirmSlotWithOrWithoutFutureModal({
        header: I18n.t("papp.products.EditProductView.StepAvailability.remove_single_slot_confirm.title"),
        content: I18n.t("papp.products.EditProductView.StepAvailability.remove_single_slot_confirm.message"),
        subConfirmButton: I18n.t(
          "papp.products.EditProductView.StepAvailability.remove_single_slot_confirm.select_all_slots",
          { all_slots_initial_date: shownInitialFutureDate }
        ),
        mainConfirmButton: I18n.t("papp.products.EditProductView.StepAvailability.remove_single_slot_confirm.confirm"),
      }).then((data) => {
        if (data) {
          const { mainConfirm } = data;
          if (mainConfirm) {
            removeSlot(initialEditedSlot, { withAllFutureSlots: false });
          } else {
            setIsScheduleBeingEdited(true);
          }
        }
      });
    } else {
      if (initialEditedSlot.booked) {
        st.confirm({
          content: I18n.t("papp.products.EditProductView.StepAvailability.remove_booked_slot_alert.message"),
          confirmButton: I18n.t(
            "papp.products.EditProductView.StepAvailability.remove_booked_slot_alert.go_to_slot_view"
          ),
        }).then((yes) => {
          yes && openShowSlotModal(initialEditedSlot);
        });
      } else {
        createConfirmSlotWithOrWithoutFutureModal({
          header: I18n.t("papp.products.EditProductView.StepAvailability.remove_slot_confirm.title"),
          content: I18n.t("papp.products.EditProductView.StepAvailability.remove_slot_confirm.message"),
          mainConfirmButton: I18n.t("papp.products.EditProductView.StepAvailability.remove_slot_confirm.confirm"),
        }).then((data) => {
          if (data) {
            removeSlot(initialEditedSlot, { withAllFutureSlots: false });
          }
        });
      }
    }
  }

  handleSlotSubmit() {
    const { slots, initialEditedSlot, editedSlot, isScheduleBeingEdited, save, setIsScheduleBeingEdited } = this.props;
    // const slotsToRemove = !initialEditedSlot || !initialEditedSlot.product_schedule_id ? [] : slots
    //   .filter(sl => sl.product_schedule_id === initialEditedSlot.product_schedule_id)
    //   .filter(sl => moment(sl.starts_at).isAfter(initialEditedSlot.starts_at))
    const { editedSlots, allSlotsWithEditedOnes, editedScheduleSlots, slotsToRemove } = generateSlots({
      slots,
      editedSlot,
      initialEditedSlot,
      forWholeSchedule: true,
    });
    const shownInitialFutureDate =
      initialEditedSlot && st.formatDate(getMinSlotStartsAt({ editedSlot, initialEditedSlot }));

    if (initialEditedSlot && isScheduleBeingEdited && (slotsToRemove.length > 1 || editedScheduleSlots.length > 1)) {
      createConfirmSlotWithOrWithoutFutureModal({
        header: I18n.t("papp.products.EditProductView.StepAvailability.edit_all_slots_confirm.title"),
        content: I18n.t("papp.products.EditProductView.StepAvailability.edit_all_slots_confirm.message"),
        mainConfirmButton: I18n.t("papp.products.EditProductView.StepAvailability.edit_all_slots_confirm.confirm", {
          all_slots_initial_date: shownInitialFutureDate,
        }),
      }).then((data) => {
        if (data) {
          save({ withAllFutureSlots: true });
        }
      });
    } else if (initialEditedSlot && (slotsToRemove.length > 1 || editedScheduleSlots.length > 1)) {
      createConfirmSlotWithOrWithoutFutureModal({
        header: I18n.t("papp.products.EditProductView.StepAvailability.edit_single_slot_confirm.title"),
        content: I18n.t("papp.products.EditProductView.StepAvailability.edit_single_slot_confirm.message"),
        subConfirmButton: I18n.t(
          "papp.products.EditProductView.StepAvailability.edit_single_slot_confirm.select_all_slots",
          { all_slots_initial_date: shownInitialFutureDate }
        ),
        mainConfirmButton: I18n.t("papp.products.EditProductView.StepAvailability.edit_single_slot_confirm.confirm"),
      }).then((data) => {
        if (data) {
          const { mainConfirm } = data;
          if (mainConfirm) {
            save({ withAllFutureSlots: false });
          } else {
            setIsScheduleBeingEdited(true);
          }
        }
      });
    } else {
      save({ withAllFutureSlots: !!editedSlot.product_schedule });
    }
  }

  renderSidebarContents() {
    const {
      readOnlyAccess,
      product,
      initialEditedSlot,
      editedSlot,
      errors,
      submitLoading,
      valid,
      validate,
      updateSlot,
      closeForm,
      isScheduleBeingEdited,
      setIsScheduleBeingEdited,
      company,
    } = this.props;

    if (!editedSlot) {
      return (
        <div>
          {!readOnlyAccess && (
            <Button.Group fluid className="u-marginBottom">
              <Button
                as="a"
                basic
                color="blue"
                content={I18n.t("papp.products.EditProductView.StepAvailability.add_slot")}
                onClick={this.handleAddNewScheduleClick.bind(this, "single")}
              />
              <Button
                as="a"
                basic
                color="blue"
                content={I18n.t("papp.products.EditProductView.StepAvailability.add_many_slots")}
                onClick={this.handleAddNewScheduleClick.bind(this, "multiple")}
              />
            </Button.Group>
          )}

          <Hint />
        </div>
      );
    } else {
      return (
        <ProductScheduleSlotForm
          enable_saas={company.enable_saas}
          readOnlyAccess={readOnlyAccess}
          key={editedSlot.uuid}
          name={`editedSlot`}
          product={product}
          slot={editedSlot}
          errors={errors}
          valid={valid}
          loading={submitLoading}
          onBlur={validate}
          onChange={updateSlot}
          onClose={closeForm}
          onRemove={initialEditedSlot && this.handleSlotDelete.bind(this)}
          removeDisabled={initialEditedSlot && !isScheduleBeingEdited && initialEditedSlot.booked}
          onSubmit={this.handleSlotSubmit.bind(this)}
          isScheduleBeingEdited={isScheduleBeingEdited}
          onScheduleFormToggle={setIsScheduleBeingEdited}
        />
      );
    }
  }

  render() {
    const { fetchLoading, fetchFailed, fetchDone, initializeView } = this.props;

    return (
      <div>
        {!fetchDone && fetchLoading && <MainView.Dimmer loading />}

        {fetchFailed && <MainView.Dimmer failed retry={initializeView} />}

        {fetchDone && (
          <div styleName="main">
            <div styleName="main-calendar">{this.renderCalendarContents()}</div>
            <div styleName="main-sidebar">{this.renderSidebarContents()}</div>
          </div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/products/EditProductView/StepAvailability/StepAvailabilityForm.jsx